import { useState, useEffect } from 'react'
import type { Practitioner } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { notifications } from '@mantine/notifications'
import { saveSignatureToPractitioner, loadSavedSignature } from '../signatureStorage'
import { logError } from './errorUtils'

/**
 * Hook that manages signature loading, saving, and modal state.
 * Shared across all vendor forms.
 */
export function useSignature() {
  const medplum = useMedplum()
  const [savedSignature, setSavedSignature] = useState<string | null>(null)
  const [signatureModalOpen, setSignatureModalOpen] = useState(false)

  useEffect(() => {
    const profile = medplum.getProfile() as Practitioner | undefined
    if (!profile?.id) return
    medplum
      .readResource('Practitioner', profile.id)
      .then((fresh) => loadSavedSignature(medplum, fresh))
      .then((dataUrl) => setSavedSignature(dataUrl))
      .catch((err) => {
        logError('Signature load', err)
      })
  }, [medplum])

  const handleSaveSignature = async (signatureDataUrl: string) => {
    const profile = medplum.getProfile() as Practitioner | undefined
    if (!profile?.id) return
    try {
      await saveSignatureToPractitioner(medplum, profile.id, signatureDataUrl)
      setSavedSignature(signatureDataUrl)
      notifications.show({
        title: 'Signature Saved',
        message: 'Your signature has been saved for future orders.',
        color: 'green',
      })
    } catch (err) {
      logError('Signature save', err)
      notifications.show({
        title: 'Signature Save Failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.',
        color: 'red',
      })
    }
  }

  return {
    savedSignature,
    signatureModalOpen,
    openSignatureModal: () => setSignatureModalOpen(true),
    closeSignatureModal: () => setSignatureModalOpen(false),
    handleSaveSignature,
  }
}
